/**
 * Employees tab table — named, per-employee monthly absence data. Gated
 * upstream by a separate, stricter RBAC permission
 * (`space.people.absence_insights.view_employees`); this component itself
 * does no access checks, it just renders whatever the caller was authorized
 * to fetch.
 *
 * Column set is derived from the union of months actually present across all
 * employee records (bounded server-side by the from/to filter), rather than
 * a separately-computed date range — a month is simply absent from an
 * employee's `monthly` array when they weren't active/reporting that month,
 * which this renders as "—", matching the prototype's wasActiveInMonth idea
 * without needing separate hire/termination-date tracking.
 *
 * Column visibility is per-session UI state only (not persisted) — Name
 * always shows (it's the row's identifier); every other column, including
 * the whole block of per-month cells as one toggle, can be hidden.
 */
import { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import type { AbsenceEmployeeRecord, AbsenceType } from '../services/absenceApi';
import { pickDays } from './AbsenceInsightsCharts';

// Percentage-based Absence Rate / Previous Year Rate columns are
// deliberately not offered — the rate calculation doesn't yet account for
// part-time FTE, which can read well over 100% for part-time staff. Day
// counts (here and per-month) are unaffected by that bug, so both the
// current and previous-year columns report in days until the rate
// calculation itself is fixed upstream (dbt_rest).
type SortKey =
  | 'name'
  | 'team'
  | 'subteam'
  | 'status'
  | 'contract'
  | 'tenure'
  | 'total'
  | 'priorYearTotal'
  | `m${string}`;

const COLUMN_DEFS = [
  { key: 'team', label: 'Team' },
  { key: 'subteam', label: 'Subteam' },
  { key: 'status', label: 'Status' },
  { key: 'contract', label: 'Contract' },
  { key: 'tenure', label: 'Tenure' },
  { key: 'monthly', label: 'Monthly cells' },
  { key: 'total', label: 'Total Days' },
  { key: 'priorYearTotal', label: 'Previous Year Total Days' },
] as const;
type ColumnKey = (typeof COLUMN_DEFS)[number]['key'];
const ALL_COLUMN_KEYS: ColumnKey[] = COLUMN_DEFS.map((c) => c.key);

function monthKey(year: number, month: number): string {
  return `${year}-${month}`;
}

/**
 * Colors a day count relative to the highest value in the currently-visible
 * set (post search/filter), not a fixed threshold — a fixed cutoff like
 * "10+ days = red" would mean wildly different things depending on how long
 * a date range is selected, since raw day counts (unlike a rate) aren't
 * normalized for period length. 0 is always neutral; nonzero values split
 * into thirds of however high the current max actually is.
 */
function relativeDayCountClass(value: number, maxValue: number): string {
  if (value === 0 || maxValue === 0) return 'bg-cotton text-steel';
  const fraction = value / maxValue;
  if (fraction < 1 / 3) return 'bg-lightGreen text-green';
  if (fraction < 2 / 3) return 'bg-lightOrange text-orange';
  return 'bg-lightRed text-red';
}

function ColumnsMenu({ visible, onChange }: { visible: Set<ColumnKey>; onChange: (next: Set<ColumnKey>) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const toggle = (key: ColumnKey) => {
    const next = new Set(visible);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex h-8 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 text-[13px] text-zinc-700 hover:border-zinc-300"
      >
        Columns
        <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full z-20 mt-1 min-w-[11rem] rounded-lg border border-zinc-200 bg-white py-1 shadow-lg">
          {COLUMN_DEFS.map(({ key, label }) => (
            <label key={key} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] text-zinc-700 hover:bg-zinc-50">
              <input
                type="checkbox"
                checked={visible.has(key)}
                onChange={() => toggle(key)}
                className="h-3.5 w-3.5 accent-sky-500"
              />
              <span>{label}</span>
            </label>
          ))}
          <div className="flex justify-between border-t border-zinc-100 px-2.5 pt-1.5">
            <button
              type="button"
              className="text-[11px] font-medium text-sky-600 hover:underline"
              onClick={() => onChange(new Set(ALL_COLUMN_KEYS))}
            >
              Show all
            </button>
            <button
              type="button"
              className="text-[11px] font-medium text-sky-600 hover:underline"
              onClick={() => onChange(new Set())}
            >
              Hide all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EmployeesTable({
  employees,
  absenceType,
}: {
  employees: AbsenceEmployeeRecord[];
  absenceType: AbsenceType;
}) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('total');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(new Set(ALL_COLUMN_KEYS));

  const months = useMemo(() => {
    const byKey = new Map<string, { year: number; month: number; monthLabel: string }>();
    for (const e of employees) {
      for (const m of e.monthly) {
        byKey.set(monthKey(m.year, m.month), m);
      }
    }
    return [...byKey.values()].sort((a, b) => a.year - b.year || a.month - b.month);
  }, [employees]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? employees.filter((e) =>
          [e.employeeName, e.department, e.team, e.subteam, e.supervisor ?? ''].some((v) =>
            v.toLowerCase().includes(q)
          )
        )
      : employees;

    const monthlyByKey = (e: AbsenceEmployeeRecord) => {
      const map = new Map(e.monthly.map((m) => [monthKey(m.year, m.month), m]));
      return map;
    };

    return [...filtered].sort((a, b) => {
      if (sortKey === 'total') {
        return sortDir * (pickDays(sumEmployee(a), absenceType) - pickDays(sumEmployee(b), absenceType));
      }
      if (sortKey === 'priorYearTotal') {
        return sortDir * (priorYearTotalDays(a, absenceType) - priorYearTotalDays(b, absenceType));
      }
      if (sortKey.startsWith('m')) {
        const key = sortKey.slice(1);
        const av = monthlyByKey(a).get(key);
        const bv = monthlyByKey(b).get(key);
        const ar = av ? pickDays(av, absenceType) : -1;
        const br = bv ? pickDays(bv, absenceType) : -1;
        return sortDir * (ar - br);
      }
      const field: Record<string, keyof AbsenceEmployeeRecord> = {
        name: 'employeeName',
        team: 'team',
        subteam: 'subteam',
        status: 'employeeStatus',
        contract: 'contractType',
        tenure: 'tenureBand',
      };
      const f = field[sortKey];
      const av = String(a[f] ?? '');
      const bv = String(b[f] ?? '');
      return sortDir * av.localeCompare(bv);
    });
  }, [employees, search, sortKey, sortDir, absenceType]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'team' || key === 'subteam' ? 1 : -1);
    }
  };

  const sortArrow = (key: SortKey) => (key === sortKey ? (sortDir === 1 ? ' ↑' : ' ↓') : '');

  if (employees.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
        <p className="text-sm text-zinc-500">No employees in the selected window.</p>
      </div>
    );
  }

  const showMonthly = visibleColumns.has('monthly');
  const showTotal = visibleColumns.has('total');
  const showPriorYearTotal = visibleColumns.has('priorYearTotal');

  const grandDays = rows.reduce((sum, e) => sum + pickDays(sumEmployee(e), absenceType), 0);
  const maxTotalDays = Math.max(...rows.map((e) => pickDays(sumEmployee(e), absenceType)), 0);

  // Employees with no prior-year rows simply contribute 0, same as being
  // absent from `rows`.
  const grandPriorDays = rows.reduce((sum, e) => sum + (e.priorYear ? pickDays(e.priorYear, absenceType) : 0), 0);
  // Excludes employees with no prior-year rows entirely (not a real 0) so
  // they can't flatten the comparison for everyone else.
  const maxPriorYearTotalDays = Math.max(
    ...rows.filter((e) => e.priorYear).map((e) => pickDays(e.priorYear!, absenceType)),
    0
  );

  // Per-month totals row — sum every currently-visible employee's days for
  // that month.
  const monthlyTotals = showMonthly
    ? months.map((m) => {
        let days = 0;
        for (const e of rows) {
          const point = e.monthly.find((mm) => mm.year === m.year && mm.month === m.month);
          if (point) days += pickDays(point, absenceType);
        }
        return { days };
      })
    : [];

  const th = (label: string, key: SortKey, center = false) => (
    <th
      className={`cursor-pointer select-none whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 hover:text-zinc-700 ${center ? 'text-center' : 'text-left'}`}
      onClick={() => toggleSort(key)}
    >
      {label}
      {sortArrow(key)}
    </th>
  );

  // Name always shows; count it plus every visible non-monthly metadata
  // column for the totals row's leading colspan.
  const leadingColSpan =
    1 + (['team', 'subteam', 'status', 'contract', 'tenure'] as const).filter((k) => visibleColumns.has(k)).length;

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, team, or subteam…"
          className="w-full max-w-sm rounded-md border border-zinc-200 px-3 py-1.5 text-sm outline-none focus:border-sky-400"
        />
        <ColumnsMenu visible={visibleColumns} onChange={setVisibleColumns} />
      </div>
      <p className="mb-2 text-xs text-zinc-400">Monthly cells show raw absence days</p>
      <div className="overflow-x-auto rounded-lg border border-zinc-100">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-zinc-50">
            <tr>
              {th('Name', 'name')}
              {visibleColumns.has('team') && th('Team', 'team')}
              {visibleColumns.has('subteam') && th('Subteam', 'subteam')}
              {visibleColumns.has('status') && th('Status', 'status')}
              {visibleColumns.has('contract') && th('Contract', 'contract')}
              {visibleColumns.has('tenure') && th('Tenure', 'tenure')}
              {showMonthly && months.map((m) => th(m.monthLabel, `m${monthKey(m.year, m.month)}` as SortKey, true))}
              {showTotal && th('Total Days', 'total', true)}
              {showPriorYearTotal && th('Previous Year Total Days', 'priorYearTotal', true)}
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => {
              const monthlyByKey = new Map(e.monthly.map((m) => [monthKey(m.year, m.month), m]));
              const totalDays = pickDays(sumEmployee(e), absenceType);
              const priorTotalDays = e.priorYear ? pickDays(e.priorYear, absenceType) : null;
              return (
                <tr key={e.employeeId} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-3 py-2 font-medium text-zinc-900">{e.employeeName}</td>
                  {visibleColumns.has('team') && <td className="px-3 py-2 text-zinc-600">{e.team}</td>}
                  {visibleColumns.has('subteam') && <td className="px-3 py-2 text-zinc-600">{e.subteam}</td>}
                  {visibleColumns.has('status') && (
                    <td className="px-3 py-2 text-zinc-600">{e.employeeStatus ?? '—'}</td>
                  )}
                  {visibleColumns.has('contract') && (
                    <td className="px-3 py-2 text-xs text-zinc-500">{e.contractType ?? '—'}</td>
                  )}
                  {visibleColumns.has('tenure') && <td className="px-3 py-2 text-xs text-zinc-500">{e.tenureBand}</td>}
                  {showMonthly &&
                    months.map((m) => {
                      const point = monthlyByKey.get(monthKey(m.year, m.month));
                      if (!point) {
                        return (
                          <td key={monthKey(m.year, m.month)} className="px-3 py-2 text-center text-zinc-300">
                            —
                          </td>
                        );
                      }
                      const days = pickDays(point, absenceType);
                      return (
                        <td key={monthKey(m.year, m.month)} className="px-3 py-2 text-center font-semibold text-zinc-900">
                          {days}d
                        </td>
                      );
                    })}
                  {showTotal && (
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block min-w-[2.5rem] rounded px-1.5 py-0.5 text-xs font-semibold ${relativeDayCountClass(totalDays, maxTotalDays)}`}>
                        {totalDays}
                      </span>
                    </td>
                  )}
                  {showPriorYearTotal && (
                    <td className="px-3 py-2 text-center">
                      {priorTotalDays === null ? (
                        <span className="text-zinc-300">—</span>
                      ) : (
                        <span className={`inline-block min-w-[2.5rem] rounded px-1.5 py-0.5 text-xs font-semibold ${relativeDayCountClass(priorTotalDays, maxPriorYearTotalDays)}`}>
                          {priorTotalDays}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            <tr className="border-t-2 border-zinc-200 bg-sky-50/40 font-semibold">
              <td colSpan={leadingColSpan} className="px-3 py-2 text-zinc-700">
                {rows.length} employees
              </td>
              {showMonthly &&
                months.map((m, i) => (
                  <td key={monthKey(m.year, m.month)} className="px-3 py-2 text-center">
                    {monthlyTotals[i].days}d
                  </td>
                ))}
              {showTotal && <td className="px-3 py-2 text-center text-zinc-900">{grandDays}</td>}
              {showPriorYearTotal && <td className="px-3 py-2 text-center text-zinc-900">{grandPriorDays}</td>}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function sumEmployee(e: AbsenceEmployeeRecord) {
  return e.monthly.reduce(
    (acc, m) => ({
      sickLeaveDays: acc.sickLeaveDays + m.sickLeaveDays,
      childCareDays: acc.childCareDays + m.childCareDays,
      totalAbsenceDays: acc.totalAbsenceDays + m.totalAbsenceDays,
      availableWorkingDays: acc.availableWorkingDays + m.availableWorkingDays,
    }),
    { sickLeaveDays: 0, childCareDays: 0, totalAbsenceDays: 0, availableWorkingDays: 0 }
  );
}

/** -1 (sorts to one end) when the employee has no prior-year rows at all, distinct from a real 0. */
function priorYearTotalDays(e: AbsenceEmployeeRecord, absenceType: AbsenceType): number {
  return e.priorYear ? pickDays(e.priorYear, absenceType) : -1;
}

